import { advancedFiltersAnimations } from './advanced-filters.animations';
import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatSelectionList } from '@angular/material/list';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ActivatedRoute } from '@angular/router';
import { faAngleDown, faAngleUp, faBars, faCheck, faSortAmountUp } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { ResizedEvent } from 'angular-resize-event';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { PrinterModel } from 'src/app/administration/printers/printer.model';
import { ShopListModel } from 'src/app/administration/shops/shop.model';
import { PaymentModel, ProductListResponse, ProductModel } from 'src/app/documents/receipts/receipts.model';
import { ReceiptsService } from 'src/app/documents/receipts/receipts.service';
import { DashCasePipe } from 'src/app/_common/pipes/dash-case-pipe';
import { DatePipe } from '@angular/common';
import { NumberRangeControl } from '../number-range-input/number-range-input.component';
import { NumberRangeValidator } from 'src/app/_common/validators/number-range.validator';
import { TimeRangeControl } from '../time-range-input/time-range-input.component';
import { TimeRangeValidator } from 'src/app/_common/validators/time-range.validator';
import { BrandService } from 'src/app/administration/brands/brand.service';
import { BrandModel } from 'src/app/administration/brands/brand.model';
import { ConfigService } from 'src/app/_common/services/config.service';

export class SelectionListArray<T> {
  name: string;
  array: T[];
}

@Component({
  selector: 'app-advanced-filters',
  templateUrl: './advanced-filters.component.html',
  styleUrls: ['./advanced-filters.component.scss'],
  animations: advancedFiltersAnimations
})

export class AdvancedFiltersComponent implements OnInit, OnDestroy {

  faFilter = faAngleUp;
  faFilterDown = faAngleDown;
  faFilterUp = faSortAmountUp;
  faApply = faCheck;
  faMenu = faBars;
  containerTopValue: number = 0;
  containerHeight: number = 0;

  @Output() topValue: EventEmitter<number> = new EventEmitter();
  @Output() loadData = new EventEmitter<any>();
  @Output() dateRangeFilterUpdated = new EventEmitter<any>();
  @Output() shopsOrPrintersFilterUpdated = new EventEmitter<any>();

  filtersChips: any[] = [];

  collapsed: boolean = false;
  collapsedSelect: boolean = false;
  isProductsFilterEnabled: boolean = false;

  brands: BrandModel[] = [];
  selectedBrands: BrandModel[] = [];
  shops: SelectionListArray<ShopListModel> = { name: 'shops', array: [] };
  printers: SelectionListArray<PrinterModel> = { name: 'printers', array: [] };
  products: SelectionListArray<ProductModel> = null;
  payments: PaymentModel[] = [];

  availableBrands: number = 0;
  availableShops: number = 0;
  availablePrinters: number = 0;

  page: number = 0;

  hasTinNumber: boolean | null = null;
  hasSystemNumber: boolean | null = null;
  hasTransactionNumber: boolean | null = null;
  isCancelled: boolean | null = null;

  today = moment();

  filtersForm: FormGroup;
  documentNumberRangeFormControl: FormControl = new FormControl(new NumberRangeControl('', ''), { validators: [NumberRangeValidator.checkValues] });
  fiscalNumberRangeFormControl: FormControl = new FormControl(new NumberRangeControl('', ''), { validators: [NumberRangeValidator.checkValues] });
  tinNumberFormControl: FormControl = new FormControl({ value: '', disabled: true });
  systemNumberFormControl: FormControl = new FormControl('');
  transactionNumberFormControl: FormControl = new FormControl('');
  grossAmountRangeFormControl: FormControl = new FormControl(new NumberRangeControl('', ''), { validators: [NumberRangeValidator.checkValues] });
  showProductsFormControl: FormControl = new FormControl(false);
  filterProductNameFormControl = new FormControl({ value: '', disabled: true });
  dateRange = new FormGroup({
    dateFrom: new FormControl<Date | null>(null),
    dateTo: new FormControl<Date | null>(null),
  });
  timeRangeFormControl: FormControl = new FormControl(new TimeRangeControl('', ''), { validators: [TimeRangeValidator.checkValues] });
  salesStatementParametersFromGroup: FormGroup;
  salesStatementParameters = Object.entries(this.configService.settings.salesStatementSettings).filter(([key, value]) => typeof value === 'boolean');
  paymentsFormControl = new FormControl();

  focusDateRange: boolean = false;
  isShowCurrency: boolean = false;

  productSubject = new Subject<ProductModel[]>();
  shopSubject = new Subject<string>();
  printerSubject = new Subject<string>();

  shopSubscription: Subscription;
  printerSubscription: Subscription;
  debounceSubscription: Subscription;
  productsFilter: any = {};

  shopFilterValue: string;
  printerFilterValue: string;

  selectedShopIds: Set<number> = new Set();
  selectedPrinterIds: Set<number> = new Set();
  
  isInitialLoad = true;

  @ViewChild('shopsList') shopsList: MatSelectionList;
  @ViewChild('printersList') printersList: MatSelectionList;
  @ViewChild('productsList') productsList: MatSelectionList;
  @ViewChild('productsListContent', { read: ElementRef }) productsListContent: ElementRef;
  @ViewChild('productSearch') productSearch: ElementRef;

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.containerHeight = window.innerHeight - this.containerTopValue - 65;
  }

  constructor(
    private brandService: BrandService,
    private receiptService: ReceiptsService,
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private dashcasePipe: DashCasePipe,
    private datePipe: DatePipe,
    private translateService: TranslateService,
    private configService: ConfigService
  ) {
    const salesStatementParameters = {};
    this.salesStatementParameters.forEach(([key, value]) => {
      salesStatementParameters[key] = new FormControl(value);
    });

    this.salesStatementParametersFromGroup = this.formBuilder.group(salesStatementParameters);

    this.filtersForm = this.formBuilder.group({
      documentNumberRangeFormControl: this.documentNumberRangeFormControl,
      fiscalNumberRangeFormControl: this.fiscalNumberRangeFormControl,
      tinNumberFormControl: this.tinNumberFormControl,
      systemNumberFormControl: this.systemNumberFormControl,
      transactionNumberFormControl: this.transactionNumberFormControl,
      grossAmountRangeFormControl: this.grossAmountRangeFormControl,
      showProductsFormControl: this.showProductsFormControl,
      filterProductNameFormControl: this.filterProductNameFormControl,
      dateRange: this.dateRange,
      timeRangeFormControl: this.timeRangeFormControl,
      salesStatementParametersFormGroup: this.salesStatementParametersFromGroup,
    })

    this.debounceSubscription = this.productSubject.pipe(
      debounceTime(1000),
      map(() => {
        const searched = this.filterProductNameFormControl.value.trim();
        if (searched) {
          this.productsFilter['name'] = searched;
          this.loadProducts()
        } else {
          this.products.array = [];
        }
      })
    ).subscribe();

    this.shopSubscription = this.shopSubject.pipe(
      debounceTime(1000)
    ).subscribe(value => {
      this.shopFilterValue = value;
      this.loadBrands(false);
    });

    this.printerSubscription = this.printerSubject.pipe(
      debounceTime(1000)
    ).subscribe(value => {
      this.printerFilterValue = value;
      this.loadBrands(false);
    });

    this.isInitialLoad = false;
  }

  ngOnInit(): void {
    this.collapsed = false;
    this.products = { name: 'products', array: [] }
    this.loadBrands(true);

    if (this.currentNavigation() == 'receipts') {
      this.receiptService.getPayments().subscribe((response) => {
        if (response.model) {
          this.payments = response.model;
          return;
        }
        if (response.error) {
          this.toastr.error(response.error.message);
        }
      });
    }

    if (this.currentNavigation() == 'sales-statement') {
      Object.entries(this.salesStatementParametersFromGroup.controls).forEach(element => {
        if (element[1].value) {
          this.addParameter(element[0]);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.debounceSubscription.unsubscribe();
    this.shopSubscription.unsubscribe();
    this.printerSubscription.unsubscribe();
  }

  getFiltersContentMargin() {
    let size = 220;
    let margin = this.availableBrands > 1 ? 106 : 0;
    this.availableShops > 1 ? margin += size : margin;
    this.availablePrinters > 1 ? margin += size : margin;
    return margin.toString();
  }

  calcListMaxHeight(): string {
    return `calc(90vh - ${this.containerTopValue + 400}px)`;
  }

  currentNavigation(): string {
    return this.route.snapshot.url[0].path;
  }

  productsFilterEnabled(event: MatSlideToggleChange) {
    this.isProductsFilterEnabled = event.checked;

    if (event.checked) {
      this.filterProductNameFormControl.enable();
      this.productSearch.nativeElement.focus();
      return;
    }

    this.filtersChips = this.filtersChips.filter(chip => chip.name !== 'products')
    this.products.array = [];
    this.filterProductNameFormControl.setValue(null);
    this.filterProductNameFormControl.disable();
  }

  onResizeChipsHeight(event: ResizedEvent): void {
    const viewportHeight = window.innerHeight;
    const newHeight = Math.round(event.newRect.top + event.newRect.bottom);
    this.topValue.emit(newHeight);
    this.containerTopValue = newHeight;
    this.containerHeight = viewportHeight - this.containerTopValue - 65;
  }

  onRemoveChip(name: string, event: any) {
    this.filtersChips = this.filtersChips.filter(chip => chip.name !== name);

    switch (name) {
      case 'shops':
        this.shopsList.deselectAll();
        break;
      case 'printers':
        this.printersList.deselectAll();
        break;
      case 'products':
        this.productsList.deselectAll();
        break;
      case 'fiscal-number':
        this.fiscalNumberRangeFormControl.setValue(null);
        break;
      case 'document-number':
        this.documentNumberRangeFormControl.setValue(null);
        break;
      case 'time':
        this.timeRangeFormControl.setValue(null);
        break;
      case 'date':
        this.dateRange.controls['dateFrom'].setValue(null);
        this.dateRange.controls['dateTo'].setValue(null);
        break;
      case 'tin':
        this.hasTinNumber = null;
        this.tinNumberFormControl.setValue(null)
        this.tinNumberFormControl.disable();
        break;
      case 'system-number':
        this.hasSystemNumber = null;
        this.systemNumberFormControl.setValue(null)
        this.systemNumberFormControl.disable();
        break;
      case 'transaction-number':
        this.hasTransactionNumber = null;
        this.transactionNumberFormControl.setValue(null)
        this.transactionNumberFormControl.disable();
        break;
      case 'gross-amount':
        this.grossAmountRangeFormControl.setValue(null);
        break;
      case 'is-cancelled':
        this.isCancelled = null;
        break;
      case 'payments':
        this.paymentsFormControl.setValue(null);
        break;
      case 'use-business-date':
      case 'based-on-receipts':
      case 'hide-details':
      case 'hide-summary-day':
      case 'hide-summary-printer':
      case 'hide-summary-shop':
        let parameterName = this.dashCaseToCamelCase(name);
        this.salesStatementParametersFromGroup.controls[parameterName].setValue(false);
        break;
    }
    if (this.collapsed) {
      this.onApply(event);
    }
  }

  onApply(event: any) {

    event.stopPropagation();
    this.loadData.emit(this.getFilters());
    this.collapsed = true;
  }

  onSelectAll(list: MatSelectionList, model: any) {
    if (!this.isChecked(list, model) || this.isIndeterminate(list, model)) {
      list.selectAll();
    }
    else {
      if (model.name === 'shops') {
        this.printersList.deselectAll();
      }
      list.deselectAll();
    }
    this.onSelectionChange(list, model);
  }

  onSelectionChange(list: MatSelectionList, model: any) {
    if (!list || !list.selectedOptions) {
      return;
    }
    if (model && model.name === 'shops') {
      this.selectedShopIds = new Set(list.selectedOptions.selected.map(item => item.value.id));
      this.filterPrinters();
      
    } else if (model && model.name === 'printers') {
      this.selectedPrinterIds = new Set(list.selectedOptions.selected.map(item => item.value.id));
    }

    if ((model.name === 'shops' && this.availableShops <= 1) || (model.name === 'printers' && this.availablePrinters < 1)) {
      this.shopsOrPrintersFilterUpdated.emit(this.getFilters());

      return;
    }

    if (model.name === 'shops' || model.name === 'printers') {
      this.filtersChips = this.filtersChips.filter(chip => chip.name !== 'printers');
    }

    if (list.selectedOptions.selected.length === 0) {
      this.filtersChips = this.filtersChips.filter(chip => chip.name !== model.name);

      if (model && ['shops', 'printers'].includes(model.name)) {
        this.shopsOrPrintersFilterUpdated.emit(this.getFilters());
      }
      
      return;
    }
      
    if (!this.filtersChips.some(chip => chip.name == model.name)) {
      let order = 1;
      switch (model.name) {
        case 'shops':
          order = 1;
          break;
        case 'printers':
          order = 2;
          break;
        case 'products':
          order = 8;
          break;
      }

      this.pushChip({
        name: model.name,
        type: 'list',
        value: list.selectedOptions.selected.map(item => item.value.name).join(', '),
        order: order,
        ids: list.selectedOptions.selected.map(item => item.value.id),
      });
    }
    else {
      let chip = this.filtersChips.find(chip => chip.name == model.name);
      if (chip) {
        chip.value = list.selectedOptions.selected
          .sort((a, b) => a.value.name.localeCompare(b.value.name))
          .map(item => item.value.name)
          .join(', ');

        chip.ids = list.selectedOptions.selected
          .sort((a, b) => a.value.id - b.value.id)
          .map(item => item.value.id);
      }
    }

    if (model && ['shops', 'printers'].includes(model.name)) {
      this.shopsOrPrintersFilterUpdated.emit(this.getFilters());
    }
  }

  onBrandSelectedChange(array: any): void {
    this.selectedBrands = [];
    array.forEach(selectedBrand => {
      this.selectedBrands.push(this.brands.find(brand => brand.id == selectedBrand.id));
    });

    this.shops.array = [];
    this.selectedBrands.forEach(brand => {
      Array.prototype.push.apply(this.shops.array, brand.shops);
    });
  
    setTimeout(() => {
      this.restoreShopAndPrinterSelections();
    });
  }


  onScrollProducts(): void {
    this.receiptService.getProducts(++this.page, JSON.stringify(this.productsFilter))
      .pipe(map((response: ProductListResponse) => {
        if (response.model) {
          this.products.array.push(...response.model);
          return;
        }
        if (response.error) {
          this.toastr.error(response.error.message);
        }
      })).subscribe();
  }

  onOptionClick(event: any, name: any, control: any, value: any) {
    let model: boolean;
    event.preventDefault();

    if (!model) {
      control.setValue(null);
    }

    switch (name) {
      case 'tin':
        this.hasTinNumber = this.hasTinNumber === value ? null : value;
        model = this.hasTinNumber;
        this.addNumberWithOption(this.hasTinNumber, control, name, 8);
        break;
      case 'system-number':
        this.hasSystemNumber = this.hasSystemNumber === value ? null : value;
        model = this.hasSystemNumber;
        this.addNumberWithOption(this.hasSystemNumber, control, name, 8);
        break;
      case 'transaction-number':
        this.hasTransactionNumber = this.hasTransactionNumber === value ? null : value;
        model = this.hasTransactionNumber;
        this.addNumberWithOption(this.hasTransactionNumber, control, name, 8);
        break;
      case 'is-cancelled':
        this.isCancelled = this.isCancelled === value ? null : value;
        model = this.isCancelled;
        this.addOption(this.isCancelled, name, 8);
        break;
    }

    if (!model) {
      control.disable();
    } else {
      control.enable();
    }
  }

  isFiltersFormValid() {
    let printers = this.filtersChips.find((item) => item.name == 'printers');
    return this.filtersForm.valid && (printers || this.availablePrinters <= 1);
  }

  filterPrinters() {
    this.printers.array = [];

    if (!this.shopsList || !this.shopsList.selectedOptions) {
      return;
    }
    
    this.shopsList.selectedOptions.selected.forEach(selected => {
      this.shops.array.forEach(shop => {
        if (selected.value.id == shop.id) {
          Array.prototype.push.apply(this.printers.array, shop.printers);
        }
      });
    });
    
    this.availablePrinters = this.printers.array.length;
    
    setTimeout(() => {
      if (this.availablePrinters >= 1 && this.printersList) {
        this.restorePrinterSelection();
      }
      
      if (this.printersList) {
        this.onSelectionChange(this.printersList, this.printers);
      }
    });
  }

  filterProducts() {
    this.productSubject.next();
  }

  onShopInput(value: string): void {
    this.shopSubject.next(value);
  }

  onPrinterInput(value: string): void {
    this.printerSubject.next(value);
  }

  toggleSelect(event: any) {
    event.stopPropagation();
    this.collapsedSelect = !this.collapsedSelect;
  }

  toggle() {
    this.collapsed = !this.collapsed;
  }

  toggleOutside(event: any) {
    let target = event.target as HTMLElement;
    if (!this.collapsed && target.classList.contains('filters-content-container')) {
      this.collapsed = true;
    }
  }

  collapse() {
    this.collapsed = true;
  }

  removeChip(name: string) {
    let idx = this.filtersChips.findIndex(chip => chip.name == name);
    if (idx > 0) {
      this.filtersChips.splice(idx, 1);
    }
  }

  pushChip(chip: any) {
    this.filtersChips.push(chip);
    this.filtersChips.sort((a, b) => a.order - b.order);
  }

  updateFiltersChips(modifiedArray: any[]) {
    this.filtersChips = modifiedArray;
  }

  getChipName(chip: any) {
    switch (chip.type) {
      case 'bool':
        return;
      case 'date-range':
        return `${this.translateService.instant('common.date')}: `;
      case 'number-range':
        return `${this.translateService.instant('common.advanced-filters.' + chip.name + '-short')}: `;
      case 'number-with-option':
        let key = 'common.advanced-filters.' + chip.name + '-short';
        let translation = this.translateService.instant(key);
        return `${translation == key ? this.translateService.instant('common.advanced-filters.' + chip.name) : translation}: `;
      default:
        return `${this.translateService.instant('common.advanced-filters.' + chip.name)}: `;
    }
  }

  getChipValue(chip: any) {
    const fromPrefix = this.translateService.instant('common.from');
    const toPrefix = this.translateService.instant('common.to');
    switch (chip.type) {
      case 'bool':
        return this.translateService.instant('common.advanced-filters.' + chip.name);
      case 'time-range':
        let currentLang = this.translateService.currentLang;
        let format = currentLang == 'en' ? 'h:mm a' : 'HH:mm';
        let timeFrom = chip.valueFrom == '' || !chip.valueFrom ? this.translateService.instant('common.advanced-filters.beginning-of-day') : this.datePipe.transform(chip.valueFrom, format);
        let timeTo = chip.valueTo == '' || !chip.valueTo ? this.translateService.instant('common.advanced-filters.end-of-day') : this.datePipe.transform(chip.valueTo, format);
        if (timeFrom == timeTo) {
          return timeFrom;
        } else {
          return `${fromPrefix}:${timeFrom} ${toPrefix}:${timeTo}`;
        }
      case 'date-range':
        let dateFrom = !chip.dateFrom || chip.dateFrom == '' ? this.translateService.instant('common.beginning') : this.datePipe.transform(chip.dateFrom, 'yyyy-MM-dd');
        let dateTo = !chip.dateTo || chip.dateTo == '' ? this.translateService.instant('common.end') : this.datePipe.transform(chip.dateTo, 'yyyy-MM-dd');
        if (dateFrom == dateTo) {
          return dateFrom;
        } else {
          return `${fromPrefix}:${dateFrom} ${toPrefix}:${dateTo}`;
        }
      case 'number-range':
        let numberFrom = chip.valueFrom == '' ? this.translateService.instant('common.first-one') : chip.valueFrom;
        let numberTo = chip.valueTo == '' ? this.translateService.instant('common.last-one') : chip.valueTo;
        if (numberFrom == numberTo) {
          return numberFrom;
        } else {
          return `${fromPrefix}:${numberFrom} ${toPrefix}:${numberTo}`;
        }
      case 'amount-range':
        let amountFrom = chip.valueFrom == '' ? this.translateService.instant('common.advanced-filters.zero') : chip.valueFrom;
        let amountTo = chip.valueTo == '' ? this.translateService.instant('common.advanced-filters.max') : chip.valueTo;
        if (amountFrom == amountTo) {
          return amountFrom;
        } else {
          return `${fromPrefix}:${amountFrom} ${toPrefix}:${amountTo}`;
        }
      default:
        return chip.value;
    }
  }

  getChipTooltip(chip: any) {
    if (this.collapsed && chip.ids && chip.ids.length > 1) {
      return chip.value.replace(/, /g, '\n');
    }

    return null;
  }

  getSelectedDescription(id: number, model: any) {
    var item = model.find((item) => item.id == id);
    return item?.description || id;
  }

  addOption(option: boolean, name: string, order: number): void {
    let value = option ? this.translateService.instant('common.yes') : this.translateService.instant('common.no');

    const chip = this.filtersChips.find(chip => chip.name == name);

    if (chip && option === null) {
      this.removeChip(chip.name);
      return;
    }
    if (!chip) {
      this.pushChip({ name: name, type: 'option', value: value, order: order, option: option });
      return;
    }
    chip.value = value;
    chip.option = option;
  }

  addNumber(control: FormControl, type: string, name: string, order: number): void {
    const value = (control.value || '');
    const chip = this.filtersChips.find(chip => chip.name == name);

    if (value) {
      if (!chip) {
        this.pushChip({ name: name, type: type, value: value, order: order });
        return;
      }
      chip.value = value;
      return;
    }
    this.removeChip(chip.name);
  }

  addNumberWithOption(option: boolean, control: FormControl, name: string, order: number): void {
    let optionValue = this.translateService.instant('common.advanced-filters.' + name + '.option-' + (option ? 'true' : 'false'));
    optionValue = optionValue.charAt(0).toLowerCase() + optionValue.slice(1);

    let value = `${this.translateService.instant('common.advanced-filters.only')} ${optionValue}`

    if (control.value) {
      value = control.value;
    }

    const chip = this.filtersChips.find(chip => chip.name == name);

    if (chip && option === null) {
      this.removeChip(chip.name);
      return;
    }
    if (!chip) {
      this.pushChip({ name: name, type: 'number-with-option', value: value, order: order, option: option, controlValue: control.value });
      return;
    }
    chip.value = value;
    chip.controlValue = control.value;
    chip.option = option;
  }

  addNumberRange(control: FormControl<NumberRangeControl>, type: string, name: string, order: number): void {
    const chip = this.filtersChips.find(chip => chip.name == name);

    const valueFrom = control.value.from;
    const valueTo = control.value.to;

    if ((valueFrom || valueTo) && !control.errors) {
      if (!chip) {
        this.pushChip({ name: name, type: type, valueFrom: valueFrom, valueTo: valueTo, order: order });
        return;
      }
      chip.valueFrom = valueFrom;
      chip.valueTo = valueTo;
      return;
    }
    if (chip) {
      this.removeChip(chip.name);
    }
  }

  addDateRange(dateRange: FormGroup, name: string, order: number): void {
    const chip = this.filtersChips.find(chip => chip.name == name);

    const dateFrom = dateRange.value.dateFrom;
    const dateTo = dateRange.value.dateTo;

    const hasError = Object.keys(dateRange.controls).some(controlName => {
      const control = dateRange.get(controlName);
      return control && control.errors;
    });

    if ((dateFrom || dateTo) && !hasError) {
      if (!chip) {
        this.pushChip({ name: name, type: 'date-range', dateFrom: dateFrom, dateTo: dateTo, order: order });

        this.dateRangeFilterUpdated.emit(this.getFilters());
        return;
      }

      chip.dateFrom = dateFrom;
      chip.dateTo = dateTo;

      this.dateRangeFilterUpdated.emit(this.getFilters());
      
      return;
    }
    if (chip) {
      this.removeChip(chip.name);
    }
  }

  addParameter(controlName: string) {
    const value = this.salesStatementParametersFromGroup.controls[controlName].value;
    const name = this.dashcasePipe.transform(controlName);
    const chip = { name: name, type: 'bool', value: value, order: 9 };
    if (value) {
      this.pushChip(chip);
      return;
    }
    this.removeChip(chip.name);
  }

  addSelectValue(model: any, control: FormControl, name: string, order: number) {
    const value = model.filter(item => control.value.includes(item.id)).map(item => item.description).join(', ');
    const chip = this.filtersChips.find(chip => chip.name == name);

    if (value && value.length > 0) {
      if (!chip) {
        this.pushChip({
          name: name,
          type: 'list',
          value: value,
          order: order,
          ids: control.value
        });
        return;
      }
      chip.value = value;
      chip.ids = control.value;
      return;
    }
    this.removeChip(chip.name);
  }

  isChecked(list: MatSelectionList, model: any): boolean {
    if (!model || !model.array || model.array.length === 0 || !list || !list.selectedOptions) {
      return false;
    }
    return list.selectedOptions.selected && model.array.length
      && list.selectedOptions.selected.length === model.array.length;
  }

  isIndeterminate(list: MatSelectionList, model: any): boolean {
    if (!list || !model || !model.array) {
      return false;
    }

    if (list.selectedOptions.selected && list.selectedOptions.selected.length === 0) {
      return false;
    }

    return list.selectedOptions.selected && model.array.length && list.selectedOptions.selected.length
      && list.selectedOptions.selected.length < model.array.length;
  }

  isSelectedInShopList(shopId: number) {
    if (!this.shopsList || !this.shopsList.selectedOptions || !this.shopsList.selectedOptions.selected) {
      return false;
    }
    const found = this.shopsList.selectedOptions.selected.find(selected => {
      return selected.value.id == shopId;
    });

    return found != undefined;
  }

  getFilters(): object {
    let filters = {};

    if (this.availableShops == 1) {
      filters['shops'] = this.shops.array[0].id.toString();
    }

    if (this.availablePrinters == 1) {
      filters['printers'] = this.printers.array[0].id.toString();
    }
    
    this.filtersChips.forEach((filter) => {
      switch (filter.type) {
        case 'list':
          filters[this.dashCaseToCamelCase(filter.name)] = filter.ids.join(',');
          break;
        case 'time-range':
          filters[`${this.dashCaseToCamelCase(filter.name)}From`] = filter.valueFrom ? filter.valueFrom?.format('HH:mm') : null;
          filters[`${this.dashCaseToCamelCase(filter.name)}To`] = filter.valueTo ? filter.valueTo?.format('HH:mm') : null;
          break;
        case 'date-range':
          filters[`${this.dashCaseToCamelCase(filter.name)}From`] = filter.dateFrom ? moment(filter.dateFrom).format('YYYY-MM-DD') : null;
          filters[`${this.dashCaseToCamelCase(filter.name)}To`] = filter.dateTo ? moment(filter.dateTo).format('YYYY-MM-DD') : null;
          break;
        case 'option':
          filters[this.dashCaseToCamelCase(filter.name)] = filter.option;
          break;
        case 'number-with-option':
          let name = this.dashCaseToCamelCase(filter.name);
          name = name.charAt(0).toUpperCase() + name.slice(1);
          filters['has' + name] = filter.option;
          filters[this.dashCaseToCamelCase(filter.name)] = filter.controlValue;
          break;
        case 'number-range':
          filters[`${this.dashCaseToCamelCase(filter.name)}From`] = filter.valueFrom;
          filters[`${this.dashCaseToCamelCase(filter.name)}To`] = filter.valueTo;
          break;
        case 'amount-range':
          filters[`${this.dashCaseToCamelCase(filter.name)}From`] = filter.valueFrom.replace(",", ".");
          filters[`${this.dashCaseToCamelCase(filter.name)}To`] = filter.valueTo.replace(",", ".");
          break;
        default:
          filters[this.dashCaseToCamelCase(filter.name)] = filter.value;
          break;
      }
    });

    return filters;
  }

  loadProducts(): void {
    this.page = 0;
    let json: string = JSON.stringify(this.productsFilter);
    this.receiptService.getProducts(this.page, json)
      .pipe(map((response: ProductListResponse) => {
        if (response.model) {
          this.products.array = response.model;
          if (this.productsListContent && this.productsListContent.nativeElement) {
            this.productsListContent.nativeElement.scrollTop = 0;
          }
          return;
        }
        if (response.error) {
          this.toastr.error(response.error.message);
        }
      })).subscribe();
  }

  loadBrands(onInit: boolean): void {
    const filter: any = {};
    
    if (this.shopFilterValue) {
      const isNumeric = !isNaN(Number(this.shopFilterValue));
      
      if (isNumeric) {
        filter.ShopNumber = this.shopFilterValue;
      } else {
        filter.ShopName = this.shopFilterValue;
      }
    }

    if (this.printerFilterValue) {
      const pattern = /^[A-Z]{3}\d{1,10}$/i;
      const isPrinterNumber = pattern.test(this.printerFilterValue);
      
      if (isPrinterNumber) {
        filter.PrinterNumber = this.printerFilterValue;
      } else {
        filter.PrinterName = this.printerFilterValue;
      }
    }

    if (this.shopFilterValue && this.selectedShopIds.size > 0){
      filter.shopsIds = Array.from(this.selectedShopIds).join(',');
    }

    if (this.printerFilterValue && this.selectedPrinterIds.size > 0){
      filter.printersIds = Array.from(this.selectedPrinterIds).join(',');
    }

    const filterToPass = Object.keys(filter).length > 0 ? filter : undefined;
  
    this.brandService.getBrandListWithFilter(filterToPass).subscribe({
      next: (response) => {
        if (response.model){          
          this.brands = response.model;

          if (onInit) {
            this.isInitialLoad = true;
            
            this.shops.array = [];
            this.printers.array = [];
            this.availableShops = 0;
            this.availablePrinters = 0;
            
            if (this.brands && this.brands.length > 0) {
              this.availableBrands = this.brands.length;
              this.brands.forEach(brand => {
                this.availableShops += brand.shops.length;
                Array.prototype.push.apply(this.shops.array, brand.shops);
              });
            }
          
            if (this.shops.array && this.shops.array.length > 0) {
              this.availableShops = this.shops.array.length;
              this.shops.array.forEach(shop => {
                this.availablePrinters += shop.printers.length;
                Array.prototype.push.apply(this.printers.array, shop.printers);
              });
            }
            
            if (!onInit) {
              this.selectedShopIds = new Set(this.shops.array.map(shop => shop.id));
              this.selectedPrinterIds = new Set(this.printers.array.map(printer => printer.id));
            }
          } else {
            this.rebuildPrintersArrayFromShops();
            this.isInitialLoad = false;
          }
            
          setTimeout(() => {
            if (this.brands.length > 0) {
              this.onBrandSelectedChange(this.brands);
            }
            this.restoreShopAndPrinterSelections();
          });
        }
      }
    }
  );
  }

  restoreShopAndPrinterSelections(): void {
    setTimeout(() => {
      if (this.isInitialLoad) {
        if (this.availableShops > 1 && this.shopsList) {
          //this.shopsList.selectAll();
          this.onSelectionChange(this.shopsList, this.shops);
        }
        if (this.availableShops == 1 && this.availablePrinters > 1 && this.printersList) {
          //this.printersList.selectAll();
          this.onSelectionChange(this.printersList, this.printers);
        }
      } else {
        this.restoreShopSelection();
        
        if (this.shopsList && this.shopsList.selectedOptions && this.shopsList.selectedOptions.selected.length === 0) {
          this.printers.array = [];
          this.availablePrinters = 0;
        } else if (this.shopsList && this.shopsList.selectedOptions && this.shopsList.selectedOptions.selected.length > 0) {
          this.filterPrinters();
        }
      }
    }, 100);
  }

  rebuildPrintersArrayFromShops(): void {
    this.printers.array = [];
    this.availablePrinters = 0;
    
    if (this.shopsList && this.shopsList.selectedOptions) {
      this.shopsList.selectedOptions.selected.forEach(selectedShop => {
        const shop = this.shops.array.find(s => s.id === selectedShop.value.id);
        if (shop && shop.printers) {
          Array.prototype.push.apply(this.printers.array, shop.printers);
          this.availablePrinters += shop.printers.length;
        }
      });
    } else {
      this.shops.array.forEach(shop => {
        this.availablePrinters += shop.printers.length;
        Array.prototype.push.apply(this.printers.array, shop.printers);
      });
    }
  }

  restoreShopSelection(): void {
    if (this.shopsList && this.selectedShopIds.size > 0) {
      this.shopsList.deselectAll();
      this.shopsList.options.forEach(option => {
        if (this.selectedShopIds.has(option.value.id)) {
          option.selected = true;
        }
      });
      this.onSelectionChange(this.shopsList, this.shops);
    }
  }
  
  restorePrinterSelection(): void {
    if (!this.printersList || !this.printersList.options) {
      return;
    }
    
    if (this.selectedPrinterIds.size > 0) {
      this.printersList.deselectAll();
      this.printersList.options.forEach(option => {
        if (this.selectedPrinterIds.has(option.value.id)) {
          option.selected = true;
        }
      });
    } else if (this.isInitialLoad) {
      this.printersList.selectAll();
    }
  }

  dashCaseToCamelCase(value: string): string {
    if (!value) {
      return value;
    }
    return value.replace(/-([a-z])/g, (match, group1) => group1.toUpperCase());
  }
}
